import { Grid, Icon, MenuItem } from "@mui/material";
import { useEffect, useState } from "react";
import { SelectValidator, TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentFormatEnum } from "../../_common/enum/documentFormat.enum";
import { EventDocumentUserRoleEnum } from "../../_common/enum/eventDocumentUserRole.enum";
import { IEventDocumentManage } from "../../_common/models/eventDocument/eventDocumentManage.model";
import { EventDocumentTemplateAutoComplete } from "../../_components/autocomplete/eventDocumentTemplate.autoComplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { eventDocumentService } from "../../_services/eventDocument.service";
import { API_URL } from "../../_config/config.const";
import { tokenService } from "../../_services/token.service";

export function EventDocumentManage(params: {
    emitOnSave: () => void,
}) {

    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [selectedEventDocumentTemplateId, setEventDocumentTemplateId] = useState(0);

    let navigate = useNavigate();

    const { eventDocumentTemplateId, eventId } = useParams<{ eventDocumentTemplateId: string, eventId: string }>();
    const _eventDocumentTemplateId = Number(eventDocumentTemplateId || 0);
    const _eventId = Number(eventId || 0);

    const [eventDocuments, setEventDocuments] = useState<Array<IEventDocumentManage>>([]);

    useEffect(() => {
        if (_eventDocumentTemplateId > 0 || _eventId > 0) fetchData();

    }, [_eventDocumentTemplateId, _eventId]);

    useEffect(() => {
        if (selectedEventDocumentTemplateId > 0) {
            fetchTemplateData();
        }

    }, [selectedEventDocumentTemplateId]);

    function fetchTemplateData() {
        setLoading(true);
        return eventDocumentService
            .getAll(_eventId, selectedEventDocumentTemplateId)
            .then(
                (json) => {
                    if (json && json.length) {
                        json.forEach((item: IEventDocumentManage) => {
                            item.id = 0;
                            item.eventDocumentTemplateId = 0;
                        });
                        setEventDocuments(json)

                    }
                }
            )
            .finally(() => setLoading(false));
    }

    function fetchData() {
        setLoading(true);
        return eventDocumentService
            .getAll(_eventId, _eventDocumentTemplateId)
            .then(
                (json) => {
                    setEventDocuments(json)
                }
            )
            .finally(() => setLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const formData = new FormData();

        if (!eventDocuments || !eventDocuments.length)
            return;

        eventDocuments.forEach((d, index) => {
            formData.append(`items[${index}].id`, (d.id || 0).toString());
            formData.append(`items[${index}].name`, d.name.toString());
            formData.append(`items[${index}].eventDocumentUserRoleId`, (d.eventDocumentUserRoleId || "").toString());
            formData.append(`items[${index}].eventDocumentTemplateId`, (eventDocumentTemplateId || "").toString());
            formData.append(`items[${index}].eventId`, (eventId || "").toString());
            formData.append(`items[${index}].documentFormatId`, (d.documentFormatId || "").toString());

            var fileInputField = document.getElementById("downloadTemplateUrl_" + index);
            if ((fileInputField as any) && (fileInputField as any)?.files && (fileInputField as any)?.files.length) {
                formData.append(`items[${index}].file`, (fileInputField as any)?.files[0]);
            }

        });

        return eventDocumentService
            .eventTemplateManage(formData)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.emitOnSave();

                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable(id: number, index: number) {
        setIsSaving(true);
        return eventDocumentService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.emitOnSave();
                    removeEventDocument(index);
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function removeEventDocument(index: number) {
        setEventDocuments((_data) => { let __data = [..._data]; __data.splice(index, 1); return __data })
    }

    function handleChange(event: any, index: number) {
        setEventDocuments((_eventDocuments) => {
            let ___eventDocuments = [..._eventDocuments];
            ___eventDocuments[index][event.target.name] = event.target.value;
            return ___eventDocuments;
        })
    };

    return (

        <ValidatorForm onSubmit={submit} noValidate>

            <Grid container spacing={1}>

                {(!eventDocuments || eventDocuments.length == 0) &&
                    <Grid item xs={12}>
                        <EventDocumentTemplateAutoComplete
                            itemId={selectedEventDocumentTemplateId}
                            name={"selectedEventDocumentTemplateId"}
                            isRequired
                            hasParent={false}
                            onItemChange={(event) => { setEventDocumentTemplateId(event.target.value) }} />
                    </Grid>
                }

                <Grid item xs={12}>
                    <PrimaryButton
                        type="button"
                        variant="success"
                        onClick={() => { setEventDocuments([...eventDocuments, {} as IEventDocumentManage]) }} >
                        <Icon>add</Icon> Add Another Document
                    </PrimaryButton>
                </Grid>

                {(eventDocuments && eventDocuments.length > 0) && eventDocuments.map((eventDocument: any, index: number) =>
                    <>
                        <Grid item xs={3}>
                            <TextValidator
                                label="Name"
                                value={eventDocument.name}
                                name="name"
                                key={"name_" + index}
                                required
                                fullWidth
                                validators={['required']}
                                errorMessages={['This field is required']}
                                onChange={(event) => { handleChange(event, index) }}
                                variant="filled"
                                InputLabelProps={{
                                    shrink: !!eventDocument.name,
                                }}
                            />
                        </Grid>

                        <Grid item xs={3}>

                            <SelectValidator
                                value={eventDocument.eventDocumentUserRoleId}
                                onChange={(event) => { handleChange(event, index) }}
                                required
                                label="Role"
                                name="eventDocumentUserRoleId"
                                fullWidth
                                key={"eventDocumentUserRoleId_" + index}
                                validators={['required']}
                                errorMessages={['This field is required']}
                                variant="filled"
                                InputLabelProps={{
                                    shrink: !!eventDocument.eventDocumentUserRoleId,
                                }}>

                                <MenuItem value={EventDocumentUserRoleEnum.Staff.toString()}>{EventDocumentUserRoleEnum[EventDocumentUserRoleEnum.Staff]}</MenuItem>
                                <MenuItem value={EventDocumentUserRoleEnum.Organiser.toString()}>{EventDocumentUserRoleEnum[EventDocumentUserRoleEnum.Organiser]}</MenuItem>

                            </SelectValidator>
                        </Grid>

                        <Grid item xs={3}>
                            <SelectValidator
                                value={eventDocument.documentFormatId}
                                onChange={(event) => { handleChange(event, index) }}
                                required
                                label="Type"
                                name="documentFormatId"
                                fullWidth
                                key={"documentFormatId_" + index}
                                validators={['required']}
                                errorMessages={['This field is required']}
                                variant="filled"
                                InputLabelProps={{
                                    shrink: !!eventDocument.documentFormatId,
                                }}>

                                <MenuItem value={DocumentFormatEnum.Any.toString()}>{DocumentFormatEnum[DocumentFormatEnum.Any]}</MenuItem>
                                <MenuItem value={DocumentFormatEnum.Image.toString()}>{DocumentFormatEnum[DocumentFormatEnum.Image]}</MenuItem>

                            </SelectValidator>
                        </Grid>


                        <Grid item xs={3}>

                            <TextValidator
                                label="Template"
                                value={eventDocument._downloadTemplateUrl}
                                name="downloadTemplateUrl"
                                type="file"
                                key={"downloadTemplateUrl_" + index}
                                id={"downloadTemplateUrl_" + index}
                                fullWidth
                                onChange={(event) => { handleChange(event, index) }}
                                variant="filled"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                        </Grid>
                        <Grid item xs={9}>

                            {eventDocument.uploadUrl &&
                                <PrimaryButton
                                    styles={{ "height": "100%" }}
                                    type="button"
                                    variant="info"
                                    onClick={() => {
                                        tokenService.GetReportToken().then((data) => {
                                            return window.open(API_URL + "/eventDocument/getDownload/?token=" + data + "&fileId=" + eventDocument.downloadTemplateUrl, "_blank");
                                        });
                                    }}>
                                    <Icon>download</Icon> Uploaded Document
                                </PrimaryButton>
                            }
                        </Grid>

                        <Grid item xs={3}>
                            <PrimaryButton
                                type="button"
                                variant="remove"
                                styles={{ "height": "100%" }}
                                onClick={() => {
                                    if (eventDocument.id) {
                                        if (window.confirm("Are you sure you want to delete selected event document?") == true) {
                                            disable(eventDocument.id, index)
                                        }
                                    }
                                    else {
                                        removeEventDocument(index);
                                    }
                                }} />
                        </Grid>

                        <Grid item xs={12}>
                            <hr />
                        </Grid>

                    </>
                )}

                {(eventDocuments && eventDocuments.length > 0) &&
                    <Grid item xs={12} spacing={2}>
                        <PrimaryButton isLoading={isLoading} isSaving={isSaving} type="submit" variant="save">Save</PrimaryButton>
                    </Grid>
                }
            </Grid>
        </ValidatorForm >
    );
}